import {
  effect,
  inject,
  Injectable,
  linkedSignal,
  signal,
  untracked,
  type Signal,
} from '@angular/core';
import { EpubPreferences, type IEpubPreferences } from '@readium/navigator';
import {
  applyHighContrastMode,
  getPersistedHighContrastMode,
  HIGH_CONTRAST_PRESETS,
  HighContrastMode,
  persistHighContrastMode,
  type HighContrastPreset,
} from '../../../helpers/high-contrast';
import {
  applyDocumentTheme,
  findReaderThemeByBackgroundColor,
  persistThemeId,
} from '../../../helpers/reader-themes';
import { serializeUserSettings } from '../../../helpers/settings';
import { MetadataService } from '../../metadata/metadata';
import { ReaderApi } from '../../reader-api/reader-api';
import { ViewportService } from '../../viewport/viewport';
import { ReaderNavigatorService } from '../navigator/reader-navigator';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly metadataService = inject(MetadataService);
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly readerApi = inject(ReaderApi);
  private readonly viewportService = inject(ViewportService);

  private readonly navigatorPreferences = this.navigatorService.navigatorPreferences;

  private readonly _settings = linkedSignal<EpubPreferences | undefined>(this.navigatorPreferences);
  private readonly _highContrastMode = signal<HighContrastMode>(getPersistedHighContrastMode());
  private baseTheme: HighContrastPreset | undefined;

  constructor() {
    effect(() => {
      const theme = findReaderThemeByBackgroundColor(this._settings()?.backgroundColor);
      if (!theme) return;
      persistThemeId(theme.id);
      void applyDocumentTheme(theme.id);
    });

    effect(() => {
      applyHighContrastMode(this._highContrastMode());
    });

    effect(() => {
      const loaded = this.navigatorService.resourceLoaded.value();
      const mode = this._highContrastMode();
      if (!loaded || mode === HighContrastMode.NONE) return;
      untracked(() => {
        this.baseTheme ??= SettingsService.captureBaseTheme(this.navigatorPreferences());
        void this.applyLivePreferences(HIGH_CONTRAST_PRESETS[mode]);
      });
    });
  }

  private static captureBaseTheme(
    settings: EpubPreferences | undefined,
  ): HighContrastPreset | undefined {
    if (!settings?.backgroundColor) return undefined;
    return {
      backgroundColor: settings.backgroundColor,
      textColor: settings.textColor,
      linkColor: settings.linkColor,
    };
  }

  get settings(): Signal<EpubPreferences | undefined> {
    return this._settings.asReadonly();
  }

  get highContrastMode(): Signal<HighContrastMode> {
    return this._highContrastMode.asReadonly();
  }

  discardHighContrastMode(): void {
    persistHighContrastMode(HighContrastMode.NONE);
    this._highContrastMode.set(HighContrastMode.NONE);
    this.baseTheme = undefined;
  }

  async setHighContrastMode(mode: HighContrastMode): Promise<void> {
    persistHighContrastMode(mode);

    if (mode === HighContrastMode.NONE) {
      this._highContrastMode.set(mode);
      if (this.baseTheme) await this.updateReaderSettings(this.baseTheme);
      this.baseTheme = undefined;
      return;
    }

    this.baseTheme ??= SettingsService.captureBaseTheme(this._settings());
    this._highContrastMode.set(mode);
  }

  private setSettings(newEpubPreferences: EpubPreferences): void {
    this._settings.set(newEpubPreferences);
  }

  private async applyLivePreferences(preferences: IEpubPreferences): Promise<EpubPreferences> {
    // eslint-disable-next-line @typescript-eslint/no-misused-spread
    const prefs = new EpubPreferences({ ...this._settings(), ...preferences });
    this.setSettings(prefs);
    await this.navigatorService.updateReaderNavigatorPreferences(prefs);
    return prefs;
  }

  async updateReaderSettings(preferences: IEpubPreferences): Promise<void> {
    const prefs = await this.applyLivePreferences(preferences);
    if (this.metadataService.isPreviewMode()) return;
    const serializedUserPreferences = serializeUserSettings(
      this.toPersistableColors(prefs),
      this.viewportService,
    );
    this.readerApi.saveUserSettings(serializedUserPreferences);
  }

  private toPersistableColors(prefs: EpubPreferences): EpubPreferences {
    if (this._highContrastMode() === HighContrastMode.NONE || !this.baseTheme) return prefs;
    // eslint-disable-next-line @typescript-eslint/no-misused-spread
    return new EpubPreferences({ ...prefs, ...this.baseTheme });
  }
}
